import { useMemo } from "react";
import { bool, number, string, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { intersectionBy } from "lodash";
import { MinAvgMaxKeys } from "@repo/utils";
import { getSectionWtClass } from "@utils";
import {
  useEstimateEditorSettings,
  useBreakdownValueAlert,
  useEstimateEditorStore,
  useEstimateStructure,
  useWorkTypePermitChecker,
  useEstimateTableSettings,
  DifferentValueAlert,
  ResetValueButton,
  ValueCell,
  BreakdownWorkloadBufferTooltip,
  TaskWorkTypeCellSettings,
} from "@tools";
import { Grid } from "@material-ui/core";
import classnames from "classnames";

export const TaskWorkType = observer(({
  element,
  workType,
  tableDepth,
  expanded,
  className,
  extendPadding,
  navigationKey,
}) => {
  
  const { allowEdition, isLibrary } = useEstimateEditorSettings();
  const { workTypeColumnsMaxState } = useEstimateTableSettings();
  const { editionGrant } = useWorkTypePermitChecker(workType.id);
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const { t } = useTranslation();
  
  const { estimateValueLevel, displayAnyValue } = structure;
  const {
    usedRoundDecimals,
    modifier,
    showBreakdownParentValues,
    maxValuesAsBuffer,
  } = structure.settings;
  const { lvl, hasChildren, usedBranchBuffers, displayWorkTypeTwoValues } = element;
  
  const editable = allowEdition && (!isLibrary || typeof element.id === "number");
  const visible = !structure.isWorkTypeOff(workType.id);
  const canEdit = editable && editionGrant;
  const valueVisible = visible && editionGrant;
  
  const holdsHours = element.holdsViewedHours[workType.id];
  const isDifferentPercent = element.isValueOverridden(workType.id);
  const displayTwoValues = workTypeColumnsMaxState[workType.id];
  
  const value = element.getDisplayHoursForWorkType(workType.id);
  const valueMax = element.getDisplayHoursMaxForWorkType(workType.id);
  const cleanValue = element.getUnbufferedHoursForWorkType(workType.id);
  const cleanValueMax = element.getUnbufferedHoursMaxForWorkType(workType.id);
  
  const showMaxValue = useMemo(() => (
    displayWorkTypeTwoValues[workType.id] && ((editable && !maxValuesAsBuffer) || value !== valueMax)
  ), [ displayWorkTypeTwoValues, editable, value, valueMax, maxValuesAsBuffer ]);
  
  const showHours = /*!emptyValue &&*/ displayAnyValue
    && Boolean(!element.parentHoldsHours[workType.id] || (
      element.parentHoldsNonOverriddenPercentHours[workType.id]
      && (
        workType.hasPercent
          ? intersectionBy(workType.of || [], Object.keys(element.holdsViewedHours), (v) => v.toString())?.length
          : Object.entries(element.holdsViewedHours).length
      )
    ))
  
  const displayCellContent = lvl <= estimateValueLevel && (!expanded || !hasChildren || holdsHours || lvl === estimateValueLevel);
  const blurDisplayedValue = lvl <= estimateValueLevel && showBreakdownParentValues && !displayCellContent;
  // const displayMinMaxSeparator = !emptyValue && (showHours || canEdit);
  const displayMinMaxSeparator = showHours || canEdit;
  
  const displayUsedBuffers = useMemo(() => (
    displayCellContent && editable && value && element.hasUsedWorkTypeBuffers(workType.id)
  ), [ usedBranchBuffers, displayCellContent, editable, value ]);
  
  const sectionWtClassName = useMemo(() => (
    "."+getSectionWtClass(workType.parent.id, workType.id)
  ), [ workType.id, workType.parent.id ])
  
  const workTypeName = useMemo(() => (
    structure.getWorkTypeById(workType.id)?.name
  ), [ workType.id ]);
  
  const handleValueChange = (value, isMax = false) => {
    let key = MinAvgMaxKeys.AVG;
    if(isMax)
      key = MinAvgMaxKeys.MAX;
    else if(!maxValuesAsBuffer && showMaxValue)
      key = MinAvgMaxKeys.MIN;
    const v = parseFloat((value * modifier).toPrecision(12));
    const oldValue = element.getTotalHoursPerWorkType(workType.id)?.[key];
    const oldUnbufferedValue = element.values?.get(workType.id.toString())?.[key];
    
    if(!element.holdsViewedHours[workType.id] && oldValue && element.hasChildren) {
      handleValueAlertOpen({ key, value: v });
      return true;
    }
    else if(v !== 0 && oldUnbufferedValue === v)// && !emptyValue)
      return true;
    else {
      setWorkloadValue({ key, value: v }, element.hasChildren)
      // element.setWorkloadValue(workType.id, { [key]: v }, element.hasChildren, element.hasChildren, true);
    }
    
    // handleRiskAlertOpen();
  };
  
  const setWorkloadValue = ({ key, value }, clearChildren) => {
    element.setWorkloadValue(workType.id, { [key]: value }, clearChildren, clearChildren, true);
  }
  
  const {
    valueAlertVisible,
    handleValueAlertClose,
    handleValueAlertOpen,
  } = useBreakdownValueAlert(setWorkloadValue);
  
  const recoverAutoCountData = () => {
    element.resetPercentValues(workType.id, true, false, true);
  };
  
  const handleMouseEnter = () => {
    document.querySelector(sectionWtClassName)?.classList.add("opaque");
  }
  
  const handleMouseLeave = () => {
    document.querySelector(sectionWtClassName)?.classList.remove("opaque");
  }
  
  return (
    <ValueCell
      displayContent={displayCellContent || blurDisplayedValue}
      displayMinMaxSeparator={displayMinMaxSeparator}
      emptyInputs={!showHours}
      value={value}
      valueMax={valueMax}
      placeholderMin={cleanValue}
      placeholderMax={cleanValueMax}
      displayMax={showMaxValue}
      size={displayTwoValues ? "medium" : "small"}
      onValueChange={handleValueChange}
      warning={isDifferentPercent}
      showDecimals
      navigationKey={navigationKey}
      editable={ maxValuesAsBuffer ? { min: canEdit } : canEdit}
      visible={valueVisible}
      decimals={usedRoundDecimals}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={className}
      extendPadding={extendPadding || editable || visibilityMode}
      innerClassName={classnames(
        editable && "relative",
        ((visibilityMode && workType.isHidden) || blurDisplayedValue) && "transparent-2"
      )}
      inputClassName={classnames(
        `input-${workTypeName?.toLowerCase().replace(/\//g, "-")}`
      )}
      inputMinTooltipMessage={
        displayUsedBuffers && !maxValuesAsBuffer &&
        <BreakdownWorkloadBufferTooltip
          element={element}
          cleanValue={cleanValue}
          workTypeId={workType.id}
        />
      }
      inputMaxTooltipMessage={
        displayUsedBuffers &&
        <BreakdownWorkloadBufferTooltip
          element={element}
          cleanValue={cleanValueMax}
          workTypeId={workType.id}
          isMax
        />
      }
    >
      {
        canEdit && displayCellContent && showHours &&
        <Grid
          item container
          direction="column"
          justifyContent="center"
          alignItems="center"
          className="absolute w-max"
          style={{ right: -16 }}
        >
          {
            isDifferentPercent &&
            <ResetValueButton action={recoverAutoCountData}>
              {t("views.editor.revert_value")}
            </ResetValueButton>
          }
          {
            !isLibrary &&
            <TaskWorkTypeCellSettings
              element={element}
              workType={workType}
              tableDepth={tableDepth}
            />
          }
        </Grid>
      }
      {
        canEdit && valueAlertVisible &&
        <DifferentValueAlert
          moduleLevel={lvl}
          handleValueAlertClose={handleValueAlertClose}
          hasChildAutomation={element.hasChildWorkloadCellAutomation(workType.id)}
        />
      }
    </ValueCell>
  );
});

TaskWorkType.propTypes = {
  element: object.isRequired,
  workType: object.isRequired,
  navigationKey: string.isRequired,
  textPresetClass: string,
  tableDepth: number,
  expanded: bool,
  extendPadding: bool,
};
